import { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "components/common/Button";
import { FolderListPane } from "../FolderListPane";
import { SelectedFolderPane } from "../SelectedFolderPane";
import { UNSORTED_FOLDER_OPTION } from "../constants";
import { groupPlanIdsByFolderKey, summarizeFolderDrafts } from "../utils";
import type { DropdownOption } from "components/common/Dropdown/types";
import type { FolderDraftsReviewProps } from "./types";

export const FolderDraftsReview = ({
  drafts,
  overwrite,
  planNameById,
  currentFolderNameByPlanId,
  isImporting,
  onDraftsChange,
  onOverwriteChange,
  onBack,
  onApply,
}: FolderDraftsReviewProps) => {
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState(
    drafts.folders.length > 0 ? drafts.folders[0].key : UNSORTED_FOLDER_OPTION,
  );

  const renameFolder = useCallback(
    (folderKey: string, name: string) => {
      onDraftsChange((currentDrafts) => ({
        ...currentDrafts,
        folders: currentDrafts.folders.map((folder) =>
          folder.key === folderKey ? { ...folder, name } : folder,
        ),
      }));
    },
    [onDraftsChange],
  );

  const movePlan = useCallback(
    (floorPlanId: number, folderOption: string) => {
      onDraftsChange((currentDrafts) => ({
        ...currentDrafts,
        folderKeyByPlanId: new Map(currentDrafts.folderKeyByPlanId).set(
          floorPlanId,
          folderOption === UNSORTED_FOLDER_OPTION ? null : folderOption,
        ),
      }));
    },
    [onDraftsChange],
  );

  const planIdsByFolderKey = useMemo(
    () => groupPlanIdsByFolderKey({ drafts }),
    [drafts],
  );

  const summary = useMemo(
    () =>
      summarizeFolderDrafts({ drafts, currentFolderNameByPlanId, overwrite }),
    [drafts, currentFolderNameByPlanId, overwrite],
  );

  // With overwrite, plans in another folder move like the rest.
  const staysInFolderNameByPlanId = useMemo(
    () =>
      overwrite
        ? new Map<number, string>()
        : summary.alreadyFiledFolderNameByPlanId,
    [overwrite, summary],
  );

  const folderOptions = useMemo<DropdownOption<string>[]>(
    () => [
      {
        value: UNSORTED_FOLDER_OPTION,
        label: t("smartSurveyor.folders.suggestion.leaveWhereItIs"),
      },
      ...drafts.folders.map((folder) => ({
        value: folder.key,
        label: folder.name,
      })),
    ],
    [drafts.folders, t],
  );

  const selectedFolder = useMemo(
    () => drafts.folders.find((folder) => folder.key === selectedKey) ?? null,
    [drafts.folders, selectedKey],
  );

  const alreadyFiledPlanCount = summary.alreadyFiledFolderNameByPlanId.size;
  const isApplyDisabled =
    isImporting || summary.folderCount === 0 || summary.hasBlankFolderName;

  return (
    <>
      <div className="flex h-[60vh] min-h-0 gap-4">
        <FolderListPane
          folders={drafts.folders}
          planIdsByFolderKey={planIdsByFolderKey}
          selectedKey={selectedKey}
          alreadyFiledPlanCount={alreadyFiledPlanCount}
          overwrite={overwrite}
          isDisabled={isImporting}
          onSelect={setSelectedKey}
          onOverwriteChange={onOverwriteChange}
        />
        <SelectedFolderPane
          // A new selection starts the plan list at the top and the name input from the draft.
          key={selectedKey}
          folder={selectedFolder}
          floorPlanIds={
            planIdsByFolderKey.get(
              selectedFolder === null ? null : selectedFolder.key,
            ) ?? []
          }
          planNameById={planNameById}
          staysInFolderNameByPlanId={staysInFolderNameByPlanId}
          currentFolderNameByPlanId={currentFolderNameByPlanId}
          folderOptions={folderOptions}
          isDisabled={isImporting}
          onRename={renameFolder}
          onMovePlan={movePlan}
        />
      </div>
      <div className="mt-4 flex flex-shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-3">
        <span className="mr-auto text-sm text-gray-500">
          <Trans
            i18nKey="smartSurveyor.folders.suggestion.applySummary"
            values={{
              folders: t("smartSurveyor.folders.suggestion.folderCount", {
                count: summary.folderCount,
              }),
              plans: t("smartSurveyor.folders.suggestion.planCount", {
                count: summary.movedPlanCount,
              }),
            }}
            components={{ b: <span className="font-medium text-gray-900" /> }}
          />
        </span>
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isImporting}
          startAdornment={<ArrowLeft size={16} />}
        >
          {t("smartSurveyor.common.back")}
        </Button>
        <Button
          variant="primary"
          onClick={onApply}
          disabled={isApplyDisabled}
          startAdornment={
            isImporting ? <Loader2 size={14} className="animate-spin" /> : null
          }
        >
          {t("smartSurveyor.folders.suggestion.apply")}
        </Button>
      </div>
    </>
  );
};
